import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { EducationHubExplore } from "../components/education/EducationHubExplore";
import { Header } from "../components/Header";
import {
	TaskTabsToggle,
	type TaskTabValue,
} from "../components/journey/CurrentTasks/TaskTabsToggle";
import {
	TaskViewToggle,
	type TaskViewValue,
} from "../components/journey/CurrentTasks/TaskViewToggle";
import { CustomTaskModal } from "../components/tasks/CustomTaskModal";
import { TaskCard } from "../components/tasks/TaskCard";
import { mapGetReadyTaskInstanceToRenderModel } from "../domain/getReadyTasks/mapping";
import {
	getAskEveI18nKey,
	getTaskRoute,
} from "../domain/getReadyTasks/taskRoutes";
import { getUserDisplayName, useUser } from "../hooks/useUser";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { getGetReadyTasks } from "../services/taskService";
import type { GetReadyCustomTaskInstance } from "../types/task";
import styles from "./GetReadyPage.module.css";

function LoadingSkeleton() {
	return (
		<div className={styles.skeletonContainer} aria-busy="true">
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
		</div>
	);
}

function ErrorState({
	onRetry,
	t,
}: {
	onRetry: () => void;
	t: (key: string, options?: { defaultValue?: string }) => string;
}) {
	return (
		<div className={styles.errorState} role="alert">
			<i className={`pi pi-exclamation-circle ${styles.errorIcon}`} />
			<h2 className={styles.errorTitle}>{t("error.title")}</h2>
			<p className={styles.errorDescription}>{t("error.description")}</p>
			<button type="button" className={styles.errorRetry} onClick={onRetry}>
				{t("error.retry")}
			</button>
		</div>
	);
}

/** Translates the TaskViewToggle's "list"/"grid" value to TaskCard's view prop. */
function viewToggleToCardView(
	toggleView: TaskViewValue,
): "collapsed" | "expanded" {
	return toggleView === "list" ? "collapsed" : "expanded";
}

export function GetReadyPage() {
	const { t, locale } = useClientTranslations("getReady");
	const { logEvent } = useAnalytics();
	const { data: userData } = useUser();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Controls state — local only, not persisted
	const [activeTab, setActiveTab] = useState<TaskTabValue>("ongoing");
	const [viewToggle, setViewToggle] = useState<TaskViewValue>("grid"); // "grid" = expanded (default)

	// Custom-task modal state — null = closed; { editing: null } = create mode;
	// { editing: GetReadyCustomTaskInstance } = edit mode.
	const [modalState, setModalState] = useState<{
		editing: GetReadyCustomTaskInstance | null;
	} | null>(null);

	const handleTabChange = useCallback(
		(next: TaskTabValue) => {
			setActiveTab(next);
			try {
				logEvent("get_ready_tab_changed", undefined, { tab: next });
			} catch {
				// non-blocking
			}
		},
		[logEvent],
	);

	const handleViewChange = useCallback(
		(next: TaskViewValue) => {
			setViewToggle(next);
			try {
				logEvent("get_ready_view_changed", undefined, {
					view: viewToggleToCardView(next),
				});
			} catch {
				// non-blocking
			}
		},
		[logEvent],
	);

	const buildOnNavigate = useCallback(
		(task: { id: string; taskKey: string }) => {
			const route = getTaskRoute(task);
			if (route) {
				return () => navigate(route);
			}
			return () => {
				try {
					toast(t("task.cta.comingSoon"));
				} catch {
					// non-blocking
				}
			};
		},
		[navigate, t],
	);

	const buildOnAskEve = useCallback(
		(taskKey: string) => {
			const i18nKey = getAskEveI18nKey(taskKey);
			return () => {
				if (i18nKey) {
					try {
						const question = t(i18nKey);
						navigate("/chat", { state: { autoMessage: question } });
					} catch {
						// non-blocking
						navigate("/chat");
					}
				} else {
					navigate("/chat");
				}
			};
		},
		[navigate, t],
	);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEvent("get_ready_page_viewed");
		}
	}, [logEvent]);

	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["tasks"],
		queryFn: () => getGetReadyTasks(),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	const steps = data?.steps ?? [];

	// Lookup from task id → original custom task instance (for the edit modal).
	const customTaskById = useMemo(() => {
		const map = new Map<string, GetReadyCustomTaskInstance>();
		for (const step of steps) {
			for (const task of step.tasks) {
				if (task.kind === "custom") map.set(task.id, task);
			}
		}
		return map;
	}, [steps]);

	const handleOpenCreateModal = useCallback(() => {
		setModalState({ editing: null });
		try {
			logEvent("custom_task_create_opened");
		} catch {
			// non-blocking
		}
	}, [logEvent]);

	const handleOpenEditModal = useCallback(
		(taskId: string) => {
			const instance = customTaskById.get(taskId);
			if (!instance) return;
			setModalState({ editing: instance });
			try {
				logEvent("custom_task_edit_opened");
			} catch {
				// non-blocking
			}
		},
		[customTaskById, logEvent],
	);

	const handleCloseModal = useCallback(() => setModalState(null), []);

	const handleTaskSaved = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["tasks"] });
	}, [queryClient]);

	const stepSections = useMemo(() => {
		return steps
			.slice()
			.sort((a, b) => a.order - b.order)
			.map((step) => {
				const tasks = step.tasks
					.slice()
					.sort((a, b) => a.order - b.order)
					.map((task) => mapGetReadyTaskInstanceToRenderModel(task, t, locale));

				return {
					stepKey: step.stepKey,
					order: step.order,
					tasks,
				};
			});
	}, [steps, t, locale]);

	const taskCount = steps.reduce((acc, step) => acc + step.tasks.length, 0);
	const allTasksCompleted = steps.every((step) =>
		step.tasks.every((task) => task.status === "completed"),
	);

	const isCompleted =
		!isLoading && !isError && (taskCount === 0 || allTasksCompleted);
	const userName = getUserDisplayName(userData);

	// Filter step sections by active tab
	const filteredSections = useMemo(() => {
		return stepSections
			.map((section) => ({
				...section,
				tasks: section.tasks.filter((task) =>
					activeTab === "completed"
						? task.status === "completed"
						: task.status !== "completed",
				),
			}))
			.filter((section) => section.tasks.length > 0);
	}, [stepSections, activeTab]);

	const cardView = viewToggleToCardView(viewToggle);

	const hasTasksInTab = filteredSections.some((s) => s.tasks.length > 0);

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{/* Page header: dynamic based on completion state */}
				<div className={styles.pageHeader}>
					<img
						src="/images/home/insights/prepare-insights.png"
						alt=""
						className={styles.pageIcon}
					/>
					<div className={styles.pageHeaderText}>
						<h1 className={styles.pageTitle}>
							{isCompleted
								? t("empty.title", { name: userName })
								: t("page.title")}
						</h1>
						<p className={styles.pageSubtitle}>
							{isCompleted ? t("empty.subtitle") : t("page.subtitle")}
						</p>
					</div>
				</div>

				{/* Content states */}
				{isLoading && <LoadingSkeleton />}
				{isError && <ErrorState onRetry={() => refetch()} t={t} />}
				{isCompleted && <EducationHubExplore />}

				{!isLoading && !isError && !isCompleted && (
					<div className={styles.tasksContainer}>
						{/* Title row: section title + add task button */}
						<div className={styles.sectionTitleRow}>
							<h2 className={styles.sectionTitle}>{t("section.title")}</h2>
							<button
								type="button"
								className={styles.addTaskButton}
								onClick={handleOpenCreateModal}
								aria-label={t("customTask.actions.add")}
							>
								<Plus className={styles.addTaskIcon} aria-hidden="true" />
							</button>
						</div>

						{/* Controls row: tabs + view toggle */}
						<div className={styles.controlsRow}>
							<TaskTabsToggle
								value={activeTab}
								onChange={handleTabChange}
								ongoingLabel={t("controls.tabs.ongoing")}
								completedLabel={t("controls.tabs.completed")}
							/>
							<TaskViewToggle
								value={viewToggle}
								onChange={handleViewChange}
								listLabel={t("controls.view.collapsed")}
								gridLabel={t("controls.view.expanded")}
							/>
						</div>

						{/* Task list — flat, filtered by tab; backend ordering preserved */}
						{hasTasksInTab ? (
							<div className={styles.taskList}>
								{filteredSections.flatMap((section) =>
									section.tasks.map((task) => (
										<TaskCard
											key={task.id}
											task={task}
											t={t}
											view={cardView}
											onNavigate={buildOnNavigate(task)}
											onAskEve={buildOnAskEve(task.taskKey)}
											onMoreOptions={
												task.kind === "custom"
													? () => handleOpenEditModal(task.id)
													: undefined
											}
										/>
									)),
								)}
							</div>
						) : (
							<p className={styles.emptyTabMessage}>
								{t("empty.completedTab")}
							</p>
						)}
					</div>
				)}
			</main>

			<CustomTaskModal
				isOpen={modalState !== null}
				onClose={handleCloseModal}
				editingTask={modalState?.editing ?? null}
				onSaved={handleTaskSaved}
			/>
		</div>
	);
}
